import { dispatchAdminSessionExpired } from '../admin/auth/adminAuthEvents';

type ApiPayload = {
  success?: boolean;
  error?: string | { code?: string | null; message?: string | null };
  code?: string | null;
  data?: unknown;
};

function getPayloadErrorMessage(payload: ApiPayload | null): string | null {
  if (!payload) {
    return null;
  }

  if (typeof payload.error === 'string') {
    return payload.error;
  }

  if (payload.error && typeof payload.error === 'object') {
    return payload.error.message ?? null;
  }

  return null;
}

function getPayloadErrorCode(payload: ApiPayload | null): string | null {
  if (!payload) {
    return null;
  }

  if (typeof payload.code === 'string') {
    return payload.code;
  }

  if (payload.error && typeof payload.error === 'object') {
    return payload.error.code ?? null;
  }

  return null;
}

export class ViewerRequestError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = 'ViewerRequestError';
    this.status = status;
    this.code = code;
  }
}

export class UnauthorizedViewerRequestError extends ViewerRequestError {
  constructor(message: string, code: string | null = null) {
    super(401, message, code);
    this.name = 'UnauthorizedViewerRequestError';
  }
}

export class ForbiddenViewerRequestError extends ViewerRequestError {
  constructor(message: string, code: string | null = null) {
    super(403, message, code);
    this.name = 'ForbiddenViewerRequestError';
  }
}

async function tryReadJsonPayload(response: Response): Promise<ApiPayload | null> {
  try {
    if (typeof response.json !== 'function') {
      return null;
    }

    return (await response.json()) as ApiPayload;
  } catch {
    return null;
  }
}

export async function readAdminApiJsonOrThrow<T extends ApiPayload>(response: Response): Promise<T> {
  const payload = await tryReadJsonPayload(response);

  if (!response.ok) {
    const message = getPayloadErrorMessage(payload) || `Request failed with status ${response.status}.`;
    const code = getPayloadErrorCode(payload);

    if (response.status === 401) {
      dispatchAdminSessionExpired({ source: 'admin-api' });
      throw new UnauthorizedViewerRequestError(message, code);
    }

    if (response.status === 403) {
      throw new ForbiddenViewerRequestError(message, code);
    }

    throw new ViewerRequestError(response.status, message, code);
  }

  if (!payload || payload.success !== true) {
    throw new ViewerRequestError(
      response.status,
      getPayloadErrorMessage(payload) || 'Request failed.',
      getPayloadErrorCode(payload),
    );
  }

  return payload as T;
}
